import type { FormDefinitionMapping } from "@wtg-glow/form-flow-engine";
import ActivityInvokerHelper from "ActivityInvokerHelper";
import captionService from "CaptionService";
import { TaskMessageBusEvent } from "Constants";
import { parseDate } from "DateExtensions";
import { DateTimeType } from "DateTimeConstants";
import { DeferredPromise } from "DeferredPromise";
import dialogService, { type Dialog } from "DialogService";
import { getHumanReadableName } from "EntityCalculatedPropertyExtensions";
import { getInterfaceName, getPrimaryKey } from "EntityExtensions";
import { fetchEntitiesByKeyAsync, observeAttachAndDetach } from "EntityManagerExtensions";
import { FormFlowAbortError } from "Errors";
import { type ShowFormActivity } from "FormFlowActivityInvokers/ShowForm.ts";
import { FormFlowError } from "FormFlowError";
import { buildExpressionData, type ExpressionData } from "FormFlowExpressionDataBuilder";
import type { default as FormFlowOthersModel, FormFlowOthersViewModel } from "FormFlowOthersModel";
import { isEntityFavorite, toggleEntityFavoriteAsync } from "FormFlowRecentService";
import { type FormFlowSession, type SaveListener, type StateListener } from "FormFlowSession";
import {
  ensureSavedMessageType,
  isButtonTransition,
  isControlTransition,
  isFinderTransition,
  isFinderTransitionEventArgs,
  isFooterTransition,
  isLookupTransition,
  isLookupTransitionEventArgs,
  isTextBoxTransition,
  isTextBoxTransitionEventArgs,
  type ControlTransition,
  type ControlTransitionEventArgs,
  type FooterTransition,
  type PendingChangesHandler,
  type ShellDisposeFn,
  type ShellProviderFn,
  type SubFormFlowEventArgs,
  type Transition,
} from "FormFlowTypes";
import { loadShellForFormFlowUIAsync } from "FormFlowUIShellProvider";
import type {
  ExtendedFormFlowUI,
  FormControlTransition,
  FormFlowUI,
  FormFooterTransition,
  FormTransitionResult,
} from "FormFlowUITypes";
import { trackBusyStateAsync } from "GlobalBusyStateTracker";
import log from "Log";
import MessageBus, { MessageType } from "MessageBus";
import type ModelProvider from "ModelProvider";
import { loadTemplateAsync } from "ModuleLoader";
import NotificationSummary from "NotificationSummary";
import { NotificationType } from "NotificationType";
import { type PageExtensions } from "PageExtensions";
import RuleExpressionCondition from "RuleExpressionCondition";
import RuleService from "RuleService";
import { invokeSubFormFlowAsync } from "SubFormFlowInvoker";
import type TaskViewModel from "TaskViewModel";
import { type ShellOptions } from "UIService";
import { makeEntityStrategy } from "VariableStrategyFactory";
import { type WorkflowContext } from "VueHooks/WorkflowContextHook.ts";
import { EntityState, type Entity } from "breeze-client";
import "gwResizable";
import ko, { type Observable, type Subscribable } from "knockout";

export type ConditionContext<T = unknown> = {
  entity: T;
  getConditionById(id: string): RuleExpressionCondition;
};

export interface TaskPageExtensions<T = unknown> extends PageExtensions {
  dataItem: T;
  messageBus: MessageBus;
  workflowContext: WorkflowContext;
  modelProvider?: ModelProvider;
  sessionData?: {
    configurationParentID?: string;
    clientPK?: string;
  };
}

export interface TaskShellOptions extends ShellOptions {
  pageExtensions: TaskPageExtensions;
  viewModel: TaskViewModel;
  transitionResolveHandler?: () => void;
  hideSaveCloseButtons?: boolean;
  disableSaveCancel?: boolean;
  others?: FormFlowOthersModel;
  onCompletedUri?: string;
  configurationParentID?: string;
  clientPK?: string;
}

// TODO:
// - Add workflowName/PK for "Secure this form-flow"
// - Add support for configuration mode (e.g. shellOptions needs configurationTmplPK, configurationParentID, configurationEntityPK)
// - Add to recents
// - Support behaviour in gwDataGrid, gwFinder, gwListView that opens a workflow or currently relies on TaskViewModel.
// - Handle or restrict scenario where more than 1 EntityManager in the session has changes.

export default class TaskPresenter2 {
  // Per presenter state
  private _isBusy: Observable<boolean> = ko.observable(false);
  private pendingActionHandlers: Set<PendingChangesHandler> = new Set();

  private _formFlowUI?: ExtendedFormFlowUI;

  private options: TaskShellOptions;
  // per activity state
  private _activity?: ShowFormActivity;
  // per show state
  private _session?: FormFlowSession;
  // data for footer transition expressions
  private expressionData: Observable<ExpressionData | undefined>;

  constructor(
    private shellProviderFn: ShellProviderFn,
    private disposeFn: ShellDisposeFn,
    options: TaskShellOptions,
  ) {
    this.options = options || {};
    this.expressionData = ko.observable();
  }

  get session(): FormFlowSession {
    if (!this._session) {
      throw new Error("This operation can only be performed after session is set");
    }

    return this._session;
  }

  get activity(): ShowFormActivity {
    if (!this._activity) {
      throw new Error("This operation can only be performed after activity is set");
    }
    return this._activity;
  }

  get formFlowUI(): FormFlowUI | undefined {
    return this._formFlowUI;
  }

  async showFormAsync(session: FormFlowSession, activity: ShowFormActivity): Promise<void> {
    const currentShowFormDeferred = new DeferredPromise<void>();
    await trackBusyStateAsync(
      (async (): Promise<void> => {
        this.ensureNotAborted();

        if (this._activity === activity) {
          // If showing the same activity, then we have the same formId, form/page, transitions, captionOverride, hideSaveButtons, hideCloseButtons, etc.
          // In this case, we only reset/recreate some state.
          this.cleanUpPerShow();
          this._session = session;

          await this.loadEntityAsync();
          if (this.formFlowUI) {
            this.formFlowUI.formAwaiter = currentShowFormDeferred;
          }
        } else {
          // Otherwise, if showing a different activity, or showing for the first time, we need to reset/recreate some additional state.
          this.cleanUpPerActivity();
          const transitionAwaiter = this._formFlowUI?.transitionAwaiter;

          this._activity = activity;
          const entityUrl = ko.pureComputed(() => this.getEntityUrl());
          // The entity observable should start undefined, such that loadEntityAsync can change to null.
          const entity = ko.observable();
          const notificationSummary = new NotificationSummary(
            entity,
            ko.computed({ read: this.getAllEntities, deferEvaluation: true, owner: this }),
          );
          const workflowContext = {
            entity,
            notificationSummary,
            version: 3,
          };

          const isFavorite = ko.pureComputed(() => {
            const entityValue = entity();
            return !!entityValue && isEntityFavorite(entityValue);
          });

          const toggleFavoriteAsync = (): Promise<void> => {
            const entityValue = entity();
            if (!entityValue) {
              return Promise.resolve();
            }

            return toggleEntityFavoriteAsync(entityValue);
          };

          this._session = session;
          this._formFlowUI = {
            formId: this.activity.FormId as keyof FormDefinitionMapping,
            configurationParentID: this.options.parentID ?? this.session.definitionPK,
            title: {
              captionOverride: this.activity.CaptionOverride,
              captionOverrideArgs: this.activity.CaptionOverrideArgs,
              captionMacroData: buildExpressionData(this.session),
            },
            entity,
            entityUrl,
            isFavorite,
            formAwaiter: currentShowFormDeferred,
            isBusy: this._isBusy,
            isAborted: false,
            pendingActionHandlers: this.pendingActionHandlers,
            notificationSummary,
            workflowContext,
            hasChanges: ko.pureComputed(() => this.session.hasChanges()),
            toggleFavoriteAsync,
            semaphoreTokens: this.session.semaphoreTokens,
            disabledFormActions: this.activity.DisabledFormActions,
            modelProvider: this.session.modelProvider,
            others: this.getOthersViewModel(),
            buttonConfig: {
              hideFooter: this.activity.HideFooter,
              hideSaveButtons:
                this.options.hideSaveCloseButtons || this.activity.HideSaveButtons || this.options.disableSaveCancel,
              hideCloseButtons: this.options.hideSaveCloseButtons || this.activity.HideCloseButtons,
              hideBackButtons: this.activity.HideBackButtons,
              disableSaveCancel: this.options.disableSaveCancel,
              footerTransitions: ko.pureComputed(this.getFooterTransitions, this),
            },
            clientPK: this.options.clientPK,
            uiContextOptions: this.options.uiContextOptions,
            messageBus: new MessageBus(),
            shellProviderFn: this.shellProviderFn,
            shellDisposeFn: this.disposeFn,
            getAllEntities: () => this.getAllEntities(),
            abort: () => this.abort(),
            ensureNotAborted: () => this.ensureNotAborted(),
            discardChangesAsync: () => this.session.discardEntityManagersAsync(),
            saveChangesAsync: (shouldRefresh) => this.session.saveAsync({ shouldRefresh }),
            reloadEntityAsync: () => this.loadEntityAsync(),
            getControlTransition: (controlId: string) =>
              Array.from(this.getControlTransitions(activity)).find((t) => t.controlId === controlId),
            handleSubFormFlowAsync: (eventArgs) => this.handleSubFormFlowAsync(eventArgs),
            log: (message) => this.logInfo(message),
          };
          // We need to load the entity before loading the shell, as UI extensions assume the entity has already loaded.
          try {
            await this.loadEntityAsync();
            await loadShellForFormFlowUIAsync(this._formFlowUI);
            this.subscribeToAdditionalEvents(this._formFlowUI.messageBus, this.session);
          } finally {
            this.resolveCurrentTransitionDeferred(transitionAwaiter);
          }
        }
        this.ensureNotAborted();
        await this.registerEntityUsageAsync();
      })(),
    );
    this.ensureNotAborted();
    return currentShowFormDeferred.promise;
  }

  private getEntityUrl(): string | undefined {
    return this.options.others?.uri();
  }

  dispose(): void {
    this.disposeFn();
    this.cleanUpPerActivity();
    this.cleanUpPerShow();
  }

  abort(): void {
    if (this._formFlowUI) {
      this._formFlowUI.isAborted = true;
      this.resolveCurrentTransitionDeferred();
      this.rejectCurrentShowFormDeferred();
    }
  }

  isBusy(): boolean {
    // busy if it is already running an exclusive action, or if the currentShowFormDeferred is completed (and so no further actions should happen)
    return this._formFlowUI?.isBusy!() || !this._formFlowUI?.formAwaiter || !this._formFlowUI?.formAwaiter.isPending;
  }

  resolveTransition(): void {
    this.resolveCurrentTransitionDeferred();
  }

  hasFormEntity(): boolean {
    return !!ko.unwrap(this._formFlowUI?.entity);
  }

  async ensureSavedAsync(shouldRunExclusive?: boolean): Promise<boolean> {
    const messageBus = this._formFlowUI?.messageBus;
    if (!messageBus) {
      return false;
    }

    const result = await messageBus.publishAsync(ensureSavedMessageType, { shouldRunExclusive });
    return result.every(Boolean);
  }

  // Once aborted, we need to ensure that all promises (session, transition, show form) are rejected, and that we no longer show forms.
  private ensureNotAborted(): void | never {
    if (this._formFlowUI?.isAborted) {
      throw new FormFlowAbortError();
    }
  }

  private cleanUpPerActivity(): void {
    const notificationSummary = this._formFlowUI?.notificationSummary;
    if (notificationSummary) {
      notificationSummary.dispose();
      if (ko.isComputed(notificationSummary.entities)) {
        notificationSummary.entities.dispose();
      }
    }
  }

  private cleanUpPerShow(): void {
    this.resolveCurrentTransitionDeferred();
  }

  private resolveCurrentTransitionDeferred(transitionAwaiter?: DeferredPromise<void>): void {
    transitionAwaiter = transitionAwaiter ?? this._formFlowUI?.transitionAwaiter;
    if (transitionAwaiter) {
      transitionAwaiter.resolve();
    }

    if (this.options.transitionResolveHandler) {
      this.options.transitionResolveHandler();
    }
  }

  private rejectCurrentShowFormDeferred(): void {
    if (this._formFlowUI?.formAwaiter) {
      this._formFlowUI.formAwaiter.reject(new FormFlowAbortError());
    }
  }

  private getAllEntities(): Entity[] {
    if (this._formFlowUI?.entity) {
      const entity = this._formFlowUI.entity();
      if (entity) {
        const entityState = entity.entityAspect.entityState;
        if (!entityState.isDeleted() && !entityState.isDetached()) {
          const entityManager = entity.entityAspect.entityManager;
          observeAttachAndDetach(entityManager);
          return entityManager.getEntities();
        }
      }
    }

    return [];
  }

  private getFooterTransitions(): FormFooterTransition[] {
    let context: ConditionContext;
    const footerTransitions = this.activity.Transitions.filter((transition: Transition) => {
      if (!isFooterTransition(transition)) {
        return false;
      } else if (transition.VisibilityExpression) {
        return new RuleExpressionCondition(transition.VisibilityExpression).evaluate(this.expressionData).value;
      } else if (transition.ConditionId) {
        context = context || this.getConditionContext(this.getDefinedEntity(this._formFlowUI?.entity)());
        const condition = context.getConditionById(transition.ConditionId);
        return condition.evaluate(context.entity).value;
      } else if (transition.Condition) {
        const entity = this.getDefinedEntity(this._formFlowUI?.entity)() || {};
        return new RuleExpressionCondition(transition.Condition).evaluate(entity).value;
      } else {
        return true;
      }
    }) as FooterTransition[];

    return footerTransitions.map((transition) => {
      const isDisabled = transition.DisableExpression
        ? (new RuleExpressionCondition(transition.DisableExpression).evaluate(this.expressionData).value ?? false)
        : false;

      return {
        footerTransitionId: transition.Id,
        Caption: transition.Caption,
        Category: transition.Category,
        Placement: transition.Placement,
        SuspendValidation: transition.SuspendValidation,
        Icon: transition.Icon,
        IsDisabled: isDisabled,
        Tooltip: isDisabled ? transition.TooltipWhenDisabled : transition.Tooltip,
        onTransitionAsync: () => this.session.invokeActivityAsync(transition.NextActivityId),
      };
    });
  }

  private *getControlTransitions(activity: ShowFormActivity): Generator<FormControlTransition> {
    for (const transition of activity.Transitions) {
      if (isControlTransition(transition)) {
        yield {
          controlId: transition.ControlId,
          SuspendValidation: transition.SuspendValidation,
          onTransitionAsync: (eventArgs) => this.handleControlTransitionAsync(transition, eventArgs),
        };
      }
    }
  }

  private getConditionContext(entity?: Entity | null): ConditionContext {
    const rules = RuleService.get(entity ? getInterfaceName(entity) : "IGlowMacro");
    return {
      entity: entity || {},
      getConditionById(id: string): RuleExpressionCondition {
        const expression = rules.condition(id, /*okIfNotFound: */ true);
        if (!expression) {
          throw new FormFlowError(`Expected valid condition id, but was ${id}.`);
        }

        return new RuleExpressionCondition(expression);
      },
    };
  }

  private async loadEntityAsync(): Promise<void> {
    /*! SuppressStringValidation log message */
    this.logInfo("loading entity");

    const entityVariableName = this.activity.EntityVariableName;
    let entity;
    if (entityVariableName) {
      entity = (await this.session.getVariableValueAsync(entityVariableName)) as Entity;
      ActivityInvokerHelper.ensureNotNullRecord(entity);
    } else {
      await RuleService.loadAsync("IGlowMacro");
      entity = null;
    }

    if (!this._formFlowUI?.entity) {
      throw new Error("This operation can only be performed after entity is set");
    }

    if (this._formFlowUI.entity() !== entity) {
      await this.loadTransitionDependenciesAsync(entity);
      /*! SuppressStringValidation log message */
      this.logInfo("entity updated");
      if (entity) {
        this._formFlowUI.entity(entity);
      }
    } else {
      /*! SuppressStringValidation log message */
      this.logInfo("entity retained");
    }
  }

  private async registerEntityUsageAsync(): Promise<Dialog | void> {
    const entity = this.getDefinedEntity(this._formFlowUI?.entity)();
    if (
      entity &&
      entity.entityAspect.entityState !== EntityState.Added &&
      RuleService.get(getInterfaceName(entity)).userActivityNotification()
    ) {
      const usages = await this.session.acquireSemaphoreHandleAsync(getPrimaryKey(entity));
      if (usages.length !== 0) {
        const humanReadableName = await getHumanReadableName(entity).loadAsync();
        const header = captionService.getString(
          "f047f7e1-4086-42f5-8b75-b0d611cf4b47",
          "These users are currently modifying {0} - {1}:",
          humanReadableName?.caption,
          humanReadableName?.name,
        );
        const usageMessages = usages.map((usage) => {
          return {
            usageMessage: captionService.getString(
              "8eb0fedb-b543-450f-b97b-8e8aa8d6d1b5",
              "{0} in {1} session(s) since {2}",
              usage.UserFullName,
              usage.UsageCount,
              parseDate(usage.CreateDateTimeUTC, DateTimeType.DateTimeUtc).format("D/MM/YYYY h:mm:ss A"),
            ),
          };
        });

        return dialogService.showDialogAsync({
          viewModel: { header, usageMessages },
          title: captionService.getString(
            "1b8b2fe1-cb15-451a-9bcb-46622f49c912",
            "Another session is editing the same information",
          ),
          notificationType: NotificationType.Information,
          bodyAllowHtml: true,
          /*! SuppressStringValidation Usages.ejs is a file */
          bodyDeferred: loadTemplateAsync("Usages.ejs"),
        });
      }
    }
  }

  private loadTransitionDependenciesAsync(entity: Entity | null): Promise<void[]> {
    let context: ConditionContext;
    let expressionData: ExpressionData;
    return Promise.all(
      this.activity.Transitions.map(async (transition: Transition) => {
        if (!isFooterTransition(transition)) {
          return;
        } else if (transition.VisibilityExpression) {
          if (!expressionData) {
            expressionData = buildExpressionData(this.session);
            this.expressionData(expressionData);
          }
          await new RuleExpressionCondition(transition.VisibilityExpression).evaluateAsync(expressionData);
        } else if (transition.DisableExpression) {
          if (!expressionData) {
            expressionData = buildExpressionData(this.session);
            this.expressionData(expressionData);
          }
          await new RuleExpressionCondition(transition.DisableExpression).evaluateAsync(expressionData);
        } else if (transition.ConditionId) {
          context = context || this.getConditionContext(entity);
          const condition = context.getConditionById(transition.ConditionId);
          await condition.evaluateAsync(context.entity);
        } else if (transition.Condition) {
          await new RuleExpressionCondition(transition.Condition).evaluateAsync(entity || {});
        } else {
          return;
        }
      }),
    );
  }

  private getOthersViewModel(): FormFlowOthersViewModel | undefined {
    if (!this.activity.HideCloseButtons) {
      const options = this.options;
      if (options) {
        const others = options.others;
        if (others) {
          return others.asViewModel({ onCompletedUri: options.onCompletedUri });
        }
      }
    }

    return undefined;
  }

  private getDefinedEntity(entity: ko.Subscribable<Entity | undefined> | undefined): Subscribable<Entity | undefined> {
    if (!entity) {
      throw new Error("This operation can only be performed after entity is set");
    }
    return entity;
  }

  private subscribeToAdditionalEvents(messageBus: MessageBus, session: FormFlowSession): void {
    const registerSaveHandlerMessageType = new MessageType<SaveListener>(TaskMessageBusEvent.RegisterSaveHandler);
    const unregisterSaveHandlerMessageType = new MessageType<SaveListener>(TaskMessageBusEvent.UnregisterSaveHandler);
    const registerStateHandlerMessageType = new MessageType<StateListener>(TaskMessageBusEvent.RegisterGetStateHandler);
    const unregisterStateHandlerMessageType = new MessageType<StateListener>(
      TaskMessageBusEvent.UnregisterGetStateHandler,
    );

    messageBus.subscribe(registerSaveHandlerMessageType, (callback: SaveListener) => {
      session.addSaveListener(callback);
    });
    messageBus.subscribe(unregisterSaveHandlerMessageType, (callback: SaveListener) => {
      session.removeSaveListener(callback);
    });
    messageBus.subscribe(registerStateHandlerMessageType, (callback: StateListener) => {
      session.addGetStateListener(callback);
    });
    messageBus.subscribe(unregisterStateHandlerMessageType, (callback: StateListener) => {
      session.removeGetStateListener(callback);
    });
  }

  private async handleSubFormFlowAsync(eventArgs: SubFormFlowEventArgs): Promise<FormTransitionResult | undefined> {
    const argumentValues: (Entity | Entity[])[] = [];
    if (eventArgs.entityName) {
      if (eventArgs.entityPK) {
        const entityManager = await this.session.getEntityManagerAsync(eventArgs.entityName);
        const result = await entityManager.fetchEntityByKey(
          eventArgs.entityName,
          eventArgs.entityPK,
          /*checkLocalCacheFirst*/ true,
        );
        argumentValues.push(result.entity);
      } else if (eventArgs.entityPKs) {
        const entityManager = await this.session.getEntityManagerAsync(eventArgs.entityName);
        const entityType = entityManager.metadataStore.getEntityType(eventArgs.entityName);

        const entities: Entity[] = [];
        const notCachedEntityPKs: string[] = [];
        eventArgs.entityPKs.forEach((entityPK) => {
          const entity = entityManager.getEntityByKey(eventArgs.entityName!, entityPK) as Entity;
          if (entity) {
            entities.push(entity);
          } else {
            notCachedEntityPKs.push(entityPK);
          }
        });
        if (notCachedEntityPKs.length) {
          const fetchEntities = await fetchEntitiesByKeyAsync(entityManager, entityType, eventArgs.entityPKs);
          entities.push(...fetchEntities);
        }
        argumentValues.push(entities);
      }
    }

    if (eventArgs.headerEntityPK && eventArgs.headerEntityName) {
      const entityManager = await this.session.getEntityManagerAsync(eventArgs.headerEntityName);
      const result = await entityManager.fetchEntityByKey(
        eventArgs.headerEntityName,
        eventArgs.headerEntityPK,
        /*checkLocalCacheFirst*/ true,
      );
      argumentValues.push(result.entity);
    }

    // The values stored in this.session and this.activity are overwritten with the child form when shown in page.
    // Retain the values here before invoking the sub form flow to be able to reload the parent page on return.
    const parentSession = this.session;
    const parentActivityId = this.activity.Id;

    await invokeSubFormFlowAsync(parentSession, eventArgs.formFlowPK, argumentValues, eventArgs);

    if (eventArgs.ShowInDialog) {
      // keep the parent page open
      return { remainOpen: true };
    } else {
      // reload parent page
      return await parentSession.invokeActivityAsync(parentActivityId);
    }
  }

  private async handleControlTransitionAsync(
    transition: ControlTransition,
    eventArgs: ControlTransitionEventArgs,
  ): Promise<FormTransitionResult | undefined> {
    if (isFinderTransition(transition) && isFinderTransitionEventArgs(eventArgs)) {
      if (transition.SearchTermVariableName) {
        this.session.setVariableValue(transition.SearchTermVariableName, eventArgs.searchTerm);
      }
      const queryResult = await eventArgs.strategy.findItemsAsync(
        eventArgs.searchTerm,
        eventArgs.isGS1,
        transition.ExpandPaths,
      );

      this.session.setVariableValue(transition.ResultsVariableName, queryResult.results);
      return this.session.invokeActivityAsync(transition.NextActivityId);
    } else if (isButtonTransition(transition)) {
      return this.session.invokeActivityAsync(transition.NextActivityId);
    } else if (isTextBoxTransition(transition) && isTextBoxTransitionEventArgs(eventArgs)) {
      if (transition.IsGs1VariableName) {
        this.session.setVariableValue(transition.IsGs1VariableName, eventArgs.isGs1);
      }
      this.session.setVariableValue(transition.TextVariableName, eventArgs.text);
      return this.session.invokeActivityAsync(transition.NextActivityId);
    } else if (isLookupTransition(transition) && isLookupTransitionEventArgs(eventArgs)) {
      const entityStrategy = makeEntityStrategy(eventArgs.entityName, eventArgs.entityPK);
      this.session.setVariableStrategy(transition.ResultVariableName, entityStrategy);
      return this.session.invokeActivityAsync(transition.NextActivityId);
    } else {
      this.formFlowUI?.formAwaiter.reject(new FormFlowError(`Unexpected transition kind ${transition.Kind}.`));
      return;
    }
  }

  private logInfo(event: string): void {
    log.info(
      `[Form-flow] ${event} for form-flow ${this.options.parentID} - activity ${this.activity.Id} - form ${this.activity.FormId}`,
    );
  }
}
